import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import FundingSourceAssignment from '../FundingSourceAssignment/FundingSourceAssignment.tsx';
import FundingAssignment from '../FundingAssignment/FundingAssignment.tsx';
import RecordCostAssignmentGrid from '../RecordCostAssignmentGrid/RecordCostAssignmentGrid.tsx';
import type { CostAssignmentMode } from '../RecordCostAssignmentGrid/RecordCostAssignmentGrid.tsx';
import FieldNames from '../../defaults/FieldNames.json';
import { useDispatch, useSelector } from 'react-redux';
import { CalculateDefaultFundingAcres } from '../../utils/FundingHelper';
import { getRequiredFields } from '../../utils/FeatureHelper';
import {
    setParentRowEditedCells,
    setSubRowEditedCells,
} from '../../redux/slices/displayStateSlice.jsx';
import type { RootState } from '../../redux/store';


// ─── Types ───────────────────────────────────────────────────────────────────

export interface FundingDialogProps {
    /** Whether the dialog is open. */
    open: boolean;
    /** The funding row being edited (passed to each sub-component as `selectedRow`). */
    selectedRow: any;
    /** Attributes of the parent feature (used by FundingSourceAssignment for program detection). */
    parentFeature?: Record<string, any>;
    /** Sibling funding rows (used by FundingSourceAssignment for BIL/BAR detection). */
    siblingRows?: any[];
    /**
     * The field name used as the unique row identifier when deduplicating sibling rows.
     * Defaults to `FieldNames.fundingFields.id` ("ID").
     * Pass "rowId" when consuming from a DataGrid that uses rowId as its row key.
     */
    rowIdField?: string;
    /** Called when the user clicks Cancel or closes the dialog. */
    onClose: () => void;
    /** Called with the merged values from all three sub-components when validation passes. */
    onSave: (mergedValues: Record<string, any>[]) => void;
}

// loosely based on buildCurrentRecord implementation from FundingCostGrid
function formatFundingRowForSave(currentRecord, userInfo, parentAttributes, siblingRecords, rowIdField: string) {
    const recordsToUpdate = [
        ...siblingRecords.filter(
            (r) => r[rowIdField] !== currentRecord[rowIdField]
        ),
        currentRecord,
    ];

    // Work on shallow copies so we never mutate the grid's internal row objects
    const copiedRecords = recordsToUpdate.map((r) => ({ ...r }));

    copiedRecords.forEach((r) => {
        // add user info to funding record
        r[FieldNames.fundingFields.modifiedDate] = new Date().getTime();
        r[FieldNames.fundingFields.modifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
        r[FieldNames.fundingFields.isArchived] = FieldNames.yesNo.no;

        // derive join/feature linkage from parentAttributes if missing on the row
        if (!r[FieldNames.fundingFields.joinID]) {
            r[FieldNames.fundingFields.joinID] = parentAttributes[FieldNames.featureFields.joinID];
        }
        if (!r[FieldNames.fundingFields.joinFeature]) {
            r[FieldNames.fundingFields.joinFeature] = parentAttributes[FieldNames.featureFields.joinFeature];
        }

        // seed created fields for new records
        if (!r[FieldNames.fundingFields.createdBy]) {
            r[FieldNames.fundingFields.createdBy] = userInfo[FieldNames.userFields.nifcUsername];
        }
        if (!r[FieldNames.fundingFields.createdDate]) {
            r[FieldNames.fundingFields.createdDate] = new Date().getTime();
        }

        // handle special cases
        r[FieldNames.fundingFields.costCode] =
            `${r[FieldNames.fundingFields.costCenter] ? r[FieldNames.fundingFields.costCenter] : 'XXXXXXXXXX'}/${r[FieldNames.fundingFields.functionalArea] ? r[FieldNames.fundingFields.functionalArea] : 'XXXXXXXXX.XXXXXX'}/${parentAttributes && parentAttributes[FieldNames.featureFields.wbsID] ? parentAttributes[FieldNames.featureFields.wbsID] : 'XXXXXXXXXXXX'}`;
    });

    const area =
        parentAttributes[FieldNames.featureFields.joinFeature] !== 'Polygon' ||
        parentAttributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes
            ? 0
            : parentAttributes[FieldNames.featureFields.calculatedArea];

    // existing utility for recalculating funding acreages
    return CalculateDefaultFundingAcres(copiedRecords, area);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FundingDialog({
    open,
    selectedRow,
    parentFeature,
    siblingRows = [],
    rowIdField = FieldNames.fundingFields.id,
    onClose,
    onSave,
}: FundingDialogProps) {
    const dispatch = useDispatch();
    const userInfo = useSelector((state: RootState) => state.globalContext.userInfo);

    const fundingSourceRef = useRef<any>(null);
    const fundingAssignmentRef = useRef<any>(null);
    const costAssignmentRef = useRef<any>(null);

    const subRowEditedCells = useSelector((state) => state.displayState.subRowEditedCells);

    // ── Derive feature-context flags from parentFeature ───────────────────────
    const isTreatment = useMemo(() => {
        const cls = parentFeature?.[FieldNames.featureFields.class];
        return (
            cls === FieldNames.classTypes.treatment || cls === FieldNames.classTypes.actualTreatment
        );
    }, [parentFeature]);

    const isActual = useMemo(() => {
        const cls = parentFeature?.[FieldNames.featureFields.class];
        return (
            cls === FieldNames.classTypes.actualTreatment ||
            cls === FieldNames.classTypes.actualActivity
        );
    }, [parentFeature]);

    const isPoint = useMemo(() => {
        return (
            parentFeature?.[FieldNames.featureFields.joinFeature] ===
                FieldNames.joinFeatureValues.point ||
            parentFeature?.[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes
        );
    }, [parentFeature]);

    const requiredFields = useMemo(() => {
        if (!parentFeature) return [];
        return (
            getRequiredFields(
                { attributes: parentFeature },
                isTreatment,
                null,
                isActual
            ) ?? []
        );
    }, [parentFeature, isTreatment, isActual]);

    const [sourceValid, setSourceValid] = useState(false);
    const [assignmentValid, setAssignmentValid] = useState(false);
    const [costValid, setCostValid] = useState(false);
    const canApply = sourceValid && assignmentValid && costValid;

    // ── Derive cost mode from the live funding source program selection ───────
    const [liveProgram, setLiveProgram] = useState<string>(
        selectedRow?.[FieldNames.fundingFields.fundingSourceProgram] ?? ''
    );

    // When the selected program is "Contributed", the cost grid mode must be
    // driven by the parent feature's fuels/postfire program, not by the
    // contributed funding row itself.
    const costAssignmentMode: CostAssignmentMode = useMemo(() => {
        const isContributed = liveProgram === FieldNames.fundingSourcePrograms.contributed;
        const effectiveProgram = isContributed
            ? (parentFeature?.[FieldNames.featureFields.fundingSourceProgram] ?? '')
            : liveProgram;
        const isPostFire =
            effectiveProgram === FieldNames.fundingSourcePrograms.postFire ||
            (isContributed && (
                parentFeature?.Program === FieldNames.programTypes?.bar ||
                parentFeature?.Program === FieldNames.programTypes?.es
            ));
        return isPostFire ? 'postfire' : 'fuels';
    }, [liveProgram, parentFeature]);

    const handleSourceChange = useCallback((values: Record<string, any>) => {
        setLiveProgram(values?.[FieldNames.fundingFields.fundingSourceProgram] ?? '');
    }, []);

    const handleSave = useCallback(() => {
        const sourceValid = fundingSourceRef.current?.isValid() ?? false;
        const assignmentValid = fundingAssignmentRef.current?.isValid() ?? false;
        const costValid = costAssignmentRef.current?.isValid() ?? false;

        if (!sourceValid || !assignmentValid || !costValid) return;

        const sourceValues = fundingSourceRef.current?.getValues() ?? {};
        const assignmentValues = fundingAssignmentRef.current?.getValues() ?? {};
        const costValues = costAssignmentRef.current?.getValues() ?? {};

        // Shallow-copy so we never mutate the original selectedRow (which is
        // the grid's internal row object).  Mutating it would make oldRow and
        // newRow identical in processRowUpdate.
        const updatedRecord = {
            ...(selectedRow ?? {}),
            ...sourceValues,
            ...assignmentValues,
            ...costValues,
        };
        onSave(formatFundingRowForSave(updatedRecord, userInfo, parentFeature, siblingRows, rowIdField));
    }, [onSave, selectedRow, userInfo, parentFeature, siblingRows, rowIdField]);

    useEffect(() => {
        setLiveProgram(selectedRow?.[FieldNames.fundingFields.fundingSourceProgram] ?? '');
    }, [selectedRow]);

    useEffect(() => {
        if (open && siblingRows.length > 0 && subRowEditedCells.changesSavedToParent === true) {
            const rowMap = {};
            siblingRows?.forEach((r) => {
                rowMap[r.rowId] = structuredClone(r);
            });
            dispatch(
                setSubRowEditedCells({
                    existingRow: rowMap,
                    newRow: [],
                    changesSavedToParent: false,
                })
            );
            dispatch(
                setParentRowEditedCells({
                    existingRow: structuredClone(parentFeature),
                    newRow: [],
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Edit Funding Assignment</DialogTitle>
            <DialogContent>
                <FundingSourceAssignment
                    ref={fundingSourceRef}
                    selectedRow={selectedRow}
                    editableFeature={{ attributes: parentFeature }}
                    rows={siblingRows}
                    requiredFields={requiredFields}
                    isPoint={isPoint}
                    onChange={handleSourceChange}
                    onValidityChange={setSourceValid}
                />
                <FundingAssignment
                    ref={fundingAssignmentRef}
                    selectedRow={selectedRow}
                    requiredFields={requiredFields}
                    isPoint={isPoint}
                    onValidityChange={setAssignmentValid}
                />
                <RecordCostAssignmentGrid
                    ref={costAssignmentRef}
                    selectedRow={selectedRow}
                    requiredFields={requiredFields}
                    isPoint={isPoint}
                    mode={costAssignmentMode}
                    onValidityChange={setCostValid}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant='contained' disabled={!canApply}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
}
